/**
 * NextUpBar — the floating pill that drives a "Next up" work session
 * (docs/crm-work-queue-plan.md). Renders over every CRM page while a session
 * runs: current lead + why it's up, progress, Skip / Done / exit. An overlay
 * on purpose — it never touches ContactDetail's layout or height calcs; the
 * recruiter works in the real lead page and the bar just floats clear of it.
 *
 * Position: TOP-center, tucked under the sticky h-12 app bar at Z.overlay
 * (owner ask 2026-08-07 — at the bottom it sat on the composer and the lead
 * rail's peek, the surfaces you need while actually working the lead). The
 * list therefore opens DOWNWARD; the pill stays the fixed anchor. The lead
 * name is a link back to that lead, so a detour is one click from the work.
 *
 * Out-of-office coverage: while a teammate is out, their leads can appear in
 * this queue carrying a `coverage` ref. Two consequences are handled below.
 * (1) Every such row wears a <CoverageBadge> — silent coverage is how a lead
 * gets worked twice. (2) SNOOZE ON A COVERED LEAD DEFERS SOMEONE ELSE'S LEAD:
 * `snoozed_until` is a column on the contact, not a per-viewer preference, so
 * it hides the lead from the absent owner's Today list as well — and they are
 * not here to notice. The snooze menu therefore says so, by name, before the
 * click rather than after it.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Check, ChevronUp, ListChecks, Loader2, MoonStar, PartyPopper, SkipForward, X, Zap,
} from 'lucide-react';
import { Z } from './ui';
import CoverageBadge, { returnDayLabel } from './CoverageBadge';
import { useWorkQueue } from '../WorkQueueContext';
import {
  eligibilityChip,
  parseSnoozeDays,
  reasonLabel,
  snoozeBlockedReason,
  snoozeReturnLabel,
  snoozeUntilDays,
  SNOOZE_PICKS,
  type QueueRowStatus,
} from '../workQueueLogic';

const actionBtn = 'inline-flex min-h-[44px] items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0';

const CHIP_TONE = {
  good: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  bad: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
} as const;

/** How the lead came in — the signal that decides where they sit in the queue. */
function EligibilityChip({ incoming, muted }: { incoming: string | null | undefined; muted?: boolean }) {
  const chip = eligibilityChip(incoming);
  if (!chip) return null;
  return (
    <span
      /* no-underline: worked/skipped rows strike their name through, and a
         struck-out chip reads as broken rather than as a done row. */
      className={`ml-1.5 inline-flex flex-shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-medium leading-none no-underline ${
        muted ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' : CHIP_TONE[chip.tone]
      }`}
      title={`Landing form: ${chip.label.toLowerCase()}`}
    >
      {chip.label}
    </span>
  );
}

/** Per-row leading marker: worked, skipped, on deck, or waiting. */
function RowMark({ status }: { status: QueueRowStatus }) {
  if (status === 'done') {
    return <Check className="h-3.5 w-3.5 flex-shrink-0 text-green-600 dark:text-green-500" aria-hidden="true" />;
  }
  if (status === 'skipped') {
    return <SkipForward className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-hidden="true" />;
  }
  if (status === 'current') {
    return <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-600 dark:bg-primary-400" aria-hidden="true" />;
  }
  return <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-300 dark:bg-gray-600" aria-hidden="true" />;
}

const STATUS_WORD: Record<QueueRowStatus, string> = {
  done: 'done', skipped: 'skipped', current: 'on deck now', upcoming: 'waiting',
};

export default function NextUpBar() {
  const {
    active, finished, item, position, total, rows, done_count, goTo, done, skip, exit,
    snoozeCurrent, snoozing, snoozeError,
  } = useWorkQueue();
  const [listOpen, setListOpen] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [days, setDays] = useState('');
  const currentRowRef = useRef<HTMLButtonElement>(null);
  const snoozeRef = useRef<HTMLDivElement>(null);

  const blocked = item ? snoozeBlockedReason(item.reason.kind) : null;
  const customDays = parseSnoozeDays(days);
  // Present only while the lead on deck belongs to someone who is out today.
  const covering = item?.coverage || null;
  const coveredOwner = covering?.owner_name?.trim().split(/\s+/)[0] || 'a teammate';

  // Both dropdowns hang off the same pill, so only one is ever open.
  const toggleList = () => { setSnoozeOpen(false); setListOpen((o) => !o); };
  const toggleSnooze = () => { setListOpen(false); setSnoozeOpen((o) => !o); };

  const fireSnooze = (d: number) => {
    setSnoozeOpen(false);
    setDays('');
    snoozeCurrent(snoozeUntilDays(d));
  };

  // Advancing to another lead must not leave a stale menu hanging over it.
  useEffect(() => { setSnoozeOpen(false); setDays(''); }, [item?.contact_id]);

  // Outside click closes the snooze menu (the list is closed by its own toggle
  // or by picking a row, so it does not need this).
  useEffect(() => {
    if (!snoozeOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!snoozeRef.current?.contains(e.target as Node)) setSnoozeOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [snoozeOpen]);

  // Opening the list should land you on the row you are working, not at the
  // top of a long queue.
  useEffect(() => {
    if (listOpen) currentRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [listOpen, position]);

  // Escape closes whichever menu is open (bubble phase — ConfirmDialog owns
  // capture). Also fires while the days box has focus, which is where a
  // recruiter backing out is most likely to be.
  useEffect(() => {
    if (!listOpen && !snoozeOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setListOpen(false);
      setSnoozeOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [listOpen, snoozeOpen]);

  if (!active) return null;

  return (
    <div
      ref={snoozeRef}
      role="region"
      aria-label="Next up work session"
      /* top-14 = the sticky app bar (h-12) plus a 0.5rem gap, on every route
         and breakpoint — the app bar is the same height everywhere. */
      className={`fixed left-1/2 top-14 ${Z.overlay} w-max max-w-[calc(100vw-1.5rem)] -translate-x-1/2`}
    >
      <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-white py-1.5 pl-3 pr-1.5 shadow-lg dark:border-gray-700 dark:bg-gray-800">
        {finished ? (
          <>
            <PartyPopper className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              All clear — nothing needs attention
            </span>
            <button
              type="button"
              onClick={() => exit(true)}
              className={`${actionBtn} bg-primary-600 text-white hover:bg-primary-700`}
            >
              Back to Today
            </button>
          </>
        ) : (
          <>
            <Zap className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            <div className="min-w-0 max-w-[16rem] sm:max-w-xs">
              {/* The name is the way back to the lead: the bar floats over
                  every CRM page, so once you detour (Today, the calendar, a
                  search) one click returns you to the lead you are working. */}
              <div className="truncate text-sm font-medium leading-tight text-gray-900 dark:text-gray-100">
                {item?.href ? (
                  <Link
                    to={item.href}
                    title={`Open ${item.name}`}
                    className="rounded hover:text-primary-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:text-primary-300"
                  >
                    {item.name}
                  </Link>
                ) : item?.name}
                {item?.study_title && (
                  <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500">{item.study_title}</span>
                )}
                <EligibilityChip incoming={item?.incoming_eligibility} />
              </div>
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-xs leading-tight text-gray-500 dark:text-gray-400">
                  {item ? reasonLabel(item.reason) : ''}
                </span>
                {/* Whose lead this really is — the recruiter must never mistake
                    a covered row for one reassigned to them. */}
                <CoverageBadge coverage={covering} compact />
              </div>
            </div>
            {/* The progress counter IS the list toggle — the affordance the
                bar was missing: "3 of 17" is the obvious place to look for
                the other 16. */}
            <button
              type="button"
              onClick={toggleList}
              aria-expanded={listOpen}
              aria-label={listOpen ? 'Hide the list' : `Show the list — ${position} of ${total}`}
              title={listOpen ? 'Hide the list' : 'Show the whole list'}
              className="inline-flex min-h-[44px] flex-shrink-0 items-center gap-1 rounded-full border border-gray-300 px-2.5 text-xs font-medium tabular-nums text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0 sm:py-1.5"
            >
              {listOpen
                ? <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                : <ListChecks className="h-3.5 w-3.5" aria-hidden="true" />}
              {position} of {total}
            </button>
            {/* Snooze — the durable "nothing needed here". Done only clears
                the lead for THIS sitting; this one keeps them off the queue
                until the day you pick. Label collapses to the icon on phones,
                where the pill is already tight. */}
            <button
              type="button"
              onClick={toggleSnooze}
              disabled={!!blocked || snoozing}
              aria-expanded={snoozeOpen}
              aria-haspopup="true"
              title={
                blocked
                || (covering
                  ? `Nothing needed now — put this lead out for a while. This is ${coveredOwner}'s lead: snoozing hides it from them too.`
                  : 'Nothing needed now — put this lead out for a while')
              }
              className={`${actionBtn} border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700`}
            >
              {snoozing
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                : <MoonStar className="h-3.5 w-3.5" aria-hidden="true" />}
              <span className="hidden sm:inline">Snooze</span>
              <span className="sr-only sm:hidden">Snooze this lead</span>
            </button>
            <button
              type="button"
              onClick={skip}
              className={`${actionBtn} border border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700`}
            >
              <SkipForward className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Skip</span>
              <span className="sr-only sm:hidden">Skip this lead</span>
            </button>
            <button
              type="button"
              onClick={done}
              className={`${actionBtn} bg-primary-600 text-white hover:bg-primary-700`}
            >
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              Done, next
            </button>
            <button
              type="button"
              onClick={() => exit()}
              aria-label="Exit work session"
              className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-300 sm:h-7 sm:w-7"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {snoozeError && (
        <div
          role="alert"
          className="ml-auto mt-2 w-max max-w-full rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
        >
          {snoozeError}
        </div>
      )}

      {/* Snooze quick-picks. Hangs below the pill like the list, right-aligned
          under its trigger; the two are mutually exclusive so they never stack. */}
      {snoozeOpen && !finished && (
        <div className="ml-auto mt-2 w-56 rounded-xl border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {/* Snoozing writes crm.contacts.snoozed_until — one column on the
              lead, not a per-viewer preference. On a covered lead that defers
              it for the ABSENT owner too, who is not here to see it happen and
              would come back to a quietly buried lead. Say so before the click.
              Deliberately a warning, not a block: deferring a covered lead is
              often the right call, it just has to be a decision. */}
          {covering && (
            <div className="mb-1 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              <AlertTriangle className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              <span>
                This is {coveredOwner}&apos;s lead
                {returnDayLabel(covering.ends_on) ? ` (${returnDayLabel(covering.ends_on)})` : ''}.
                {' '}Snoozing hides it from their list too.
              </span>
            </div>
          )}
          <div className="px-2 pb-1 pt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
            Nothing needed now — bring them back
          </div>
          {SNOOZE_PICKS.map((p) => (
            <button
              key={p.days}
              type="button"
              onClick={() => fireSnooze(p.days)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-primary-50 focus:outline-none focus-visible:bg-primary-50 dark:text-gray-200 dark:hover:bg-primary-900/20 dark:focus-visible:bg-primary-900/20"
            >
              <MoonStar className="h-3.5 w-3.5 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              {p.label}
              <span className="ml-auto text-[11px] font-normal text-gray-400 dark:text-gray-500">
                {snoozeReturnLabel(snoozeUntilDays(p.days))}
              </span>
            </button>
          ))}
          <div className="mt-1 border-t border-gray-100 px-2 pb-1 pt-2 dark:border-gray-700">
            <label
              htmlFor="nextup-snooze-days"
              className="block text-[11px] font-medium text-gray-500 dark:text-gray-400"
            >
              Put it out
            </label>
            <div className="mt-1 flex items-center gap-1.5">
              <input
                id="nextup-snooze-days"
                type="number"
                min={1}
                max={365}
                value={days}
                placeholder="14"
                onChange={(e) => setDays(e.target.value)}
                onKeyDown={(e) => {
                  // Enter is the obvious commit here; without this it would
                  // submit nothing and the menu would just sit there.
                  if (e.key === 'Enter' && customDays) {
                    e.preventDefault();
                    fireSnooze(customDays);
                  }
                }}
                className="w-14 rounded border border-gray-300 px-1.5 py-1 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">days</span>
              <button
                type="button"
                onClick={() => customDays && fireSnooze(customDays)}
                disabled={!customDays}
                className="ml-auto rounded-md bg-primary-600 px-2 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                Snooze
              </button>
            </div>
            <p className="mt-1 min-h-[14px] text-[11px] text-gray-400 dark:text-gray-500">
              {customDays ? `Back on ${snoozeReturnLabel(snoozeUntilDays(customDays))}` : ''}
            </p>
          </div>
        </div>
      )}

      {/* The queue as an actual list: what you have worked, what is on deck,
          and what is still waiting — with any row clickable to jump there.
          Hangs BELOW the pill, which stays the fixed anchor under the app bar. */}
      {listOpen && !finished && (
        <div className="mt-2 max-h-[min(60vh,22rem)] w-[min(24rem,calc(100vw-1.5rem))] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <div className="sticky top-0 flex items-center justify-between gap-2 border-b border-gray-100 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
              Your list
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {done_count} of {total} done
            </span>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.map((row, i) => (
              <li key={row.item.key}>
                <button
                  type="button"
                  ref={row.status === 'current' ? currentRowRef : undefined}
                  onClick={() => { goTo(i); setListOpen(false); }}
                  aria-current={row.status === 'current' ? 'true' : undefined}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20 ${
                    row.status === 'current' ? 'bg-primary-50/60 dark:bg-primary-900/20' : ''
                  }`}
                >
                  <RowMark status={row.status} />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block truncate text-sm ${
                        row.status === 'done' || row.status === 'skipped'
                          ? 'text-gray-400 line-through dark:text-gray-500'
                          : 'font-medium text-gray-900 dark:text-gray-100'
                      }`}
                    >
                      {row.item.name}
                      <EligibilityChip
                        incoming={row.item.incoming_eligibility}
                        muted={row.status === 'done' || row.status === 'skipped'}
                      />
                    </span>
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {reasonLabel(row.item.reason)}
                      </span>
                      <CoverageBadge coverage={row.item.coverage} compact hideReturn />
                    </span>
                  </span>
                  <span className="sr-only">{STATUS_WORD[row.status]}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
